import { useState, useEffect, useCallback, useMemo, useRef, createContext, useContext } from "react";

// ============================================
// Günaydın Insights — App Analytics Dashboard
// insights.gunaydinappstudio.com
// ============================================

const API_BASE = "https://api-insights.gunaydinappstudio.com";

// --- Auth Context ---
const AuthContext = createContext(null);

function useAuth() {
  return useContext(AuthContext);
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem("insights_auth");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setUser(parsed.user);
        setToken(parsed.accessToken);
      } catch {}
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const res = await fetch(`${API_BASE}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    setUser(data.user);
    setToken(data.accessToken);
    localStorage.setItem("insights_auth", JSON.stringify(data));
    return data;
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("insights_auth");
  };

  const setup = async (email, name, password) => {
    const res = await fetch(`${API_BASE}/auth/setup`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, name, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, setup }}>
      {children}
    </AuthContext.Provider>
  );
}

// --- API Hook ---
function useAPI() {
  const { token } = useAuth();

  const fetchAPI = useCallback(async (path, options = {}) => {
    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    return data;
  }, [token]);

  return { fetchAPI };
}

// --- Sparkline SVG ---
function Sparkline({ data, color = "var(--accent)", width = 120, height = 32 }) {
  if (!data || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(" ");

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: "block" }}>
      <defs>
        <linearGradient id={`grad-${color.replace(/[^a-z0-9]/gi, "")}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <polygon
        points={`0,${height} ${points} ${width},${height}`}
        fill={`url(#grad-${color.replace(/[^a-z0-9]/gi, "")})`}
      />
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// --- Donut Chart ---
function DonutChart({ value, max = 100, size = 140, strokeWidth = 10, color }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(value / max, 1);
  const dashOffset = circumference * (1 - progress);

  const getColor = () => {
    if (color) return color;
    if (value >= 70) return "#22c55e";
    if (value >= 40) return "#eab308";
    return "#ef4444";
  };

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={strokeWidth} />
      <circle
        cx={size / 2} cy={size / 2} r={radius} fill="none"
        stroke={getColor()} strokeWidth={strokeWidth}
        strokeDasharray={circumference} strokeDashoffset={dashOffset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: "stroke-dashoffset 1s ease" }}
      />
      <text x={size / 2} y={size / 2} textAnchor="middle" dominantBaseline="central" fill="white" fontSize="28" fontWeight="700" fontFamily="'Urbanist', sans-serif">
        {Math.round(value)}
      </text>
    </svg>
  );
}

// --- Bar Chart ---
function BarChart({ data, height = 200, barColor = "var(--accent)", showLabels = true }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d.value), 1);

  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: "2px", height, padding: "0 4px" }}>
      {data.map((d, i) => {
        const barH = (d.value / max) * (height - 24);
        return (
          <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
            <div style={{ width: "100%", maxWidth: "32px", height: barH, background: d.color || barColor, borderRadius: "3px 3px 0 0", transition: "height 0.5s ease", minHeight: "2px" }} />
            {showLabels && <span style={{ fontSize: "9px", color: "rgba(255,255,255,0.4)", whiteSpace: "nowrap" }}>{d.label}</span>}
          </div>
        );
      })}
    </div>
  );
}

// --- Stacked Bar ---
function StackedBar({ data, height = 200 }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => (d.new || 0) + (d.renewal || 0)), 1);

  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: "2px", height, padding: "0 4px" }}>
      {data.map((d, i) => {
        const newH = ((d.new || 0) / max) * (height - 24);
        const renH = ((d.renewal || 0) / max) * (height - 24);
        return (
          <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "0" }}>
            <div style={{ width: "100%", maxWidth: "28px", display: "flex", flexDirection: "column-reverse" }}>
              <div style={{ height: newH, background: "#00FFEF", borderRadius: "0 0 3px 3px", minHeight: d.new > 0 ? "2px" : "0" }} />
              <div style={{ height: renH, background: "rgba(0,255,239,0.3)", borderRadius: "3px 3px 0 0", minHeight: d.renewal > 0 ? "2px" : "0" }} />
            </div>
            <span style={{ fontSize: "9px", color: "rgba(255,255,255,0.4)", marginTop: "4px" }}>{d.label}</span>
          </div>
        );
      })}
    </div>
  );
}

// --- KPI Card ---
function KPICard({ title, value, change, prefix = "", suffix = "", sparkData }) {
  const isPositive = change > 0;
  const changeColor = isPositive ? "#22c55e" : change < 0 ? "#ef4444" : "rgba(255,255,255,0.4)";

  return (
    <div style={{
      background: "rgba(255,255,255,0.03)",
      borderRadius: "16px",
      padding: "20px",
      flex: 1,
      minWidth: "180px",
    }}>
      <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.45)", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {title}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: "8px", marginBottom: "4px" }}>
        <span style={{ fontSize: "28px", fontWeight: "700", color: "white" }}>
          {prefix}{typeof value === "number" ? value.toLocaleString() : value}{suffix}
        </span>
        {change !== null && change !== undefined && (
          <span style={{ fontSize: "13px", color: changeColor, fontWeight: "600" }}>
            {isPositive ? "↑" : change < 0 ? "↓" : ""}
            {Math.abs(change).toFixed(1)}%
          </span>
        )}
      </div>
      {sparkData && <Sparkline data={sparkData} color={isPositive ? "#22c55e" : "#ef4444"} />}
    </div>
  );
}

// --- Filter Bar ---
function FilterBar({ period, setPeriod, country, setCountry, plan, setPlan }) {
  const periods = [
    { label: "7g", value: 7 },
    { label: "30g", value: 30 },
    { label: "90g", value: 90 },
    { label: "Bu Yıl", value: 365 },
  ];

  return (
    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "24px" }}>
      <div style={{ display: "flex", background: "rgba(255,255,255,0.04)", borderRadius: "10px", overflow: "hidden" }}>
        {periods.map(p => (
          <button
            key={p.value}
            onClick={() => setPeriod(p.value)}
            style={{
              padding: "8px 16px",
              background: period === p.value ? "rgba(0,255,239,0.15)" : "transparent",
              border: "none",
              color: period === p.value ? "#00FFEF" : "rgba(255,255,255,0.5)",
              cursor: "pointer",
              fontSize: "13px",
              fontWeight: period === p.value ? "600" : "400",
              fontFamily: "'Urbanist', sans-serif",
              transition: "all 0.2s",
            }}
          >
            {p.label}
          </button>
        ))}
      </div>
      <select
        value={plan}
        onChange={e => setPlan(e.target.value)}
        style={{
          background: "rgba(255,255,255,0.04)",
          border: "none",
          color: "rgba(255,255,255,0.7)",
          padding: "8px 12px",
          borderRadius: "10px",
          fontSize: "13px",
          fontFamily: "'Urbanist', sans-serif",
          cursor: "pointer",
        }}
      >
        <option value="ALL">Tüm Planlar</option>
        <option value="weekly">Haftalık</option>
        <option value="annual">Yıllık</option>
      </select>
    </div>
  );
}

// --- Signal Badge ---
function SignalBadge({ level, text, detail }) {
  const colors = {
    green: { bg: "rgba(34,197,94,0.1)", border: "rgba(34,197,94,0.3)", text: "#22c55e", icon: "🟢" },
    yellow: { bg: "rgba(234,179,8,0.1)", border: "rgba(234,179,8,0.3)", text: "#eab308", icon: "🟡" },
    red: { bg: "rgba(239,68,68,0.1)", border: "rgba(239,68,68,0.3)", text: "#ef4444", icon: "🔴" },
  };
  const c = colors[level] || colors.green;

  return (
    <div style={{
      background: c.bg,
      border: `1px solid ${c.border}`,
      borderRadius: "12px",
      padding: "16px 20px",
      display: "flex",
      alignItems: "center",
      gap: "12px",
    }}>
      <span style={{ fontSize: "20px" }}>{c.icon}</span>
      <div>
        <div style={{ color: c.text, fontWeight: "700", fontSize: "15px" }}>{text}</div>
        <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "13px", marginTop: "2px" }}>{detail}</div>
      </div>
    </div>
  );
}

// --- Alert Card ---
function AlertCard({ alert }) {
  const icons = { critical: "🔴", warning: "🟡", info: "🔵" };

  return (
    <div style={{
      background: "rgba(255,255,255,0.03)",
      borderRadius: "12px",
      padding: "14px 18px",
      display: "flex",
      alignItems: "flex-start",
      gap: "12px",
      borderLeft: `3px solid ${alert.severity === "critical" ? "#ef4444" : alert.severity === "warning" ? "#eab308" : "#3b82f6"}`,
    }}>
      <span style={{ fontSize: "16px", marginTop: "2px" }}>{icons[alert.severity] || "🔵"}</span>
      <div style={{ flex: 1 }}>
        <div style={{ color: "white", fontWeight: "600", fontSize: "14px" }}>{alert.title}</div>
        <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "13px", marginTop: "4px", lineHeight: "1.5" }}>{alert.message}</div>
        <div style={{ color: "rgba(255,255,255,0.3)", fontSize: "11px", marginTop: "6px" }}>{alert.created_at}</div>
      </div>
    </div>
  );
}

// --- Country Flag ---
function getFlag(code) {
  if (!code || code.length !== 2) return "🏳️";
  return String.fromCodePoint(...[...code.toUpperCase()].map(c => 0x1F1E6 + c.charCodeAt(0) - 65));
}

// --- Quadrant Chart ---
function QuadrantChart({ countries }) {
  if (!countries || countries.length === 0) return null;

  const maxImp = Math.max(...countries.map(c => c.impressions || 0), 1);
  const maxConv = Math.max(...countries.map(c => c.convRate || 0), 0.01);
  const midImp = maxImp * 0.4;
  const midConv = maxConv * 0.4;

  return (
    <div style={{ position: "relative", width: "100%", height: "320px", background: "rgba(255,255,255,0.02)", borderRadius: "12px", overflow: "hidden" }}>
      {/* Quadrant labels */}
      <div style={{ position: "absolute", top: "8px", left: "8px", fontSize: "10px", color: "rgba(255,255,255,0.2)", textTransform: "uppercase" }}>Potansiyel</div>
      <div style={{ position: "absolute", top: "8px", right: "8px", fontSize: "10px", color: "rgba(255,255,255,0.2)", textTransform: "uppercase" }}>Yıldızlar ⭐</div>
      <div style={{ position: "absolute", bottom: "8px", left: "8px", fontSize: "10px", color: "rgba(255,255,255,0.2)", textTransform: "uppercase" }}>Zayıflar</div>
      <div style={{ position: "absolute", bottom: "8px", right: "8px", fontSize: "10px", color: "rgba(255,255,255,0.2)", textTransform: "uppercase" }}>Düşük Verim</div>

      {/* Grid lines */}
      <div style={{ position: "absolute", left: "50%", top: 0, bottom: 0, width: "1px", background: "rgba(255,255,255,0.06)" }} />
      <div style={{ position: "absolute", top: "50%", left: 0, right: 0, height: "1px", background: "rgba(255,255,255,0.06)" }} />

      {/* Axis labels */}
      <div style={{ position: "absolute", bottom: "-2px", left: "50%", transform: "translateX(-50%)", fontSize: "10px", color: "rgba(255,255,255,0.3)" }}>Impressions →</div>
      <div style={{ position: "absolute", left: "4px", top: "50%", transform: "translateY(-50%) rotate(-90deg)", fontSize: "10px", color: "rgba(255,255,255,0.3)" }}>Conv Rate →</div>

      {/* Country dots */}
      {countries.slice(0, 20).map((c, i) => {
        const x = Math.min(95, (c.impressions / maxImp) * 90 + 5);
        const y = 95 - Math.min(90, (c.convRate / maxConv) * 90 + 5);
        const size = Math.max(20, Math.min(48, (c.proceeds / Math.max(...countries.map(cc => cc.proceeds || 0), 1)) * 48 + 16));

        return (
          <div
            key={i}
            title={`${c.country}: Imp ${c.impressions?.toLocaleString()}, Conv ${(c.convRate * 100).toFixed(1)}%, Rev $${c.proceeds?.toFixed(0)}`}
            style={{
              position: "absolute",
              left: `${x}%`,
              top: `${y}%`,
              transform: "translate(-50%, -50%)",
              width: size,
              height: size,
              borderRadius: "50%",
              background: "rgba(0,255,239,0.15)",
              border: "1px solid rgba(0,255,239,0.3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: Math.max(10, size * 0.35),
              cursor: "pointer",
              transition: "transform 0.2s",
              zIndex: 10,
            }}
            onMouseEnter={e => e.currentTarget.style.transform = "translate(-50%, -50%) scale(1.3)"}
            onMouseLeave={e => e.currentTarget.style.transform = "translate(-50%, -50%) scale(1)"}
          >
            {getFlag(c.country)}
          </div>
        );
      })}
    </div>
  );
}

// --- Country Table ---
function CountryTable({ countries }) {
  if (!countries || countries.length === 0) return <div style={{ color: "rgba(255,255,255,0.3)", padding: "20px" }}>Veri yok</div>;

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
        <thead>
          <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
            {["Ülke", "Imp", "DL", "Conv", "Revenue", "Imp Δ", "DL Δ", "Conv Δ", "Rev Δ", "Trend"].map(h => (
              <th key={h} style={{ padding: "10px 12px", textAlign: "left", color: "rgba(255,255,255,0.4)", fontWeight: "500", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {countries.map((c, i) => {
            const trend = (c.revChange || 0) > 0 ? "📈" : (c.revChange || 0) < 0 ? "📉" : "➡️";
            const changeStyle = v => ({ color: v > 0 ? "#22c55e" : v < 0 ? "#ef4444" : "rgba(255,255,255,0.4)", fontWeight: "500" });
            const fmtPct = v => v !== null ? `${v > 0 ? "+" : ""}${v.toFixed(1)}%` : "—";
            const fmtRev = v => v !== null ? `${v > 0 ? "+" : ""}$${v.toFixed(0)}` : "—";

            return (
              <tr key={i} style={{ borderBottom: "1px solid rgba(255,255,255,0.03)" }}>
                <td style={{ padding: "10px 12px", color: "white", fontWeight: "600" }}>{getFlag(c.country)} {c.country}</td>
                <td style={{ padding: "10px 12px", color: "rgba(255,255,255,0.7)" }}>{(c.impressions || 0).toLocaleString()}</td>
                <td style={{ padding: "10px 12px", color: "rgba(255,255,255,0.7)" }}>{(c.downloads || 0).toLocaleString()}</td>
                <td style={{ padding: "10px 12px", color: "#00FFEF" }}>{((c.convRate || 0) * 100).toFixed(1)}%</td>
                <td style={{ padding: "10px 12px", color: "white", fontWeight: "600" }}>${(c.proceeds || 0).toFixed(0)}</td>
                <td style={{ padding: "10px 12px", ...changeStyle(c.impChange) }}>{fmtPct(c.impChange)}</td>
                <td style={{ padding: "10px 12px", ...changeStyle(c.dlChange) }}>{fmtPct(c.dlChange)}</td>
                <td style={{ padding: "10px 12px", ...changeStyle(c.convChange) }}>{fmtPct(c.convChange)}</td>
                <td style={{ padding: "10px 12px", ...changeStyle(c.revChange) }}>{fmtRev(c.revChange)}</td>
                <td style={{ padding: "10px 12px", fontSize: "18px" }}>{trend}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// --- Subscription Flow Diagram ---
function SubscriptionFlow({ funnel }) {
  if (!funnel || funnel.length === 0) return null;

  const getCount = type => (funnel.find(f => f.event_type === type)?.total || 0);

  const nodes = [
    { label: "Offer Starts", value: getCount("offer_start"), color: "rgba(0,255,239,0.2)" },
    { label: "Paid Sub Starts", value: getCount("subscribe"), color: "rgba(0,255,239,0.3)" },
    { label: "Active Plans", value: getCount("subscribe") + getCount("renew") - getCount("cancel"), color: "rgba(0,255,239,0.4)" },
    { label: "Renewals", value: getCount("renew"), color: "rgba(34,197,94,0.3)" },
    { label: "Churned", value: getCount("cancel"), color: "rgba(239,68,68,0.3)" },
  ];

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", justifyContent: "center" }}>
      {nodes.map((n, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div style={{
            background: n.color,
            borderRadius: "12px",
            padding: "16px 20px",
            textAlign: "center",
            minWidth: "100px",
          }}>
            <div style={{ fontSize: "22px", fontWeight: "700", color: "white" }}>{n.value.toLocaleString()}</div>
            <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.5)", marginTop: "4px" }}>{n.label}</div>
          </div>
          {i < nodes.length - 1 && <span style={{ color: "rgba(255,255,255,0.2)", fontSize: "18px" }}>→</span>}
        </div>
      ))}
    </div>
  );
}

// ============================================
// PAGES
// ============================================

// --- Login Page ---
function LoginPage() {
  const { login, setup } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [isSetup, setIsSetup] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setError("");
    setLoading(true);
    try {
      if (isSetup) {
        await setup(email, name, password);
        await login(email, password);
      } else {
        await login(email, password);
      }
    } catch (e) {
      setError(e.message);
      if (e.message.includes("Setup")) setIsSetup(true);
    }
    setLoading(false);
  };

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#0A0A0B",
      fontFamily: "'Urbanist', sans-serif",
    }}>
      <div style={{
        width: "100%",
        maxWidth: "400px",
        padding: "40px",
        background: "rgba(255,255,255,0.02)",
        borderRadius: "24px",
        border: "1px solid rgba(255,255,255,0.04)",
      }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", marginBottom: "8px" }}>
            <svg width="36" height="20" viewBox="0 0 36 20">
              <path d="M0 20 Q18 -4 36 20" fill="none" stroke="#00FFEF" strokeWidth="2.5" />
              <circle cx="18" cy="6" r="3" fill="#00FFEF" opacity="0.4" />
            </svg>
            <span style={{ fontSize: "20px", fontWeight: "800", color: "white", letterSpacing: "-0.5px" }}>Insights</span>
          </div>
          <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.3)" }}>GÜNAYDIN APP STUDIO</div>
        </div>

        <h2 style={{ color: "white", fontSize: "22px", fontWeight: "700", marginBottom: "24px", textAlign: "center" }}>
          {isSetup ? "İlk Kurulum" : "Giriş Yap"}
        </h2>

        {isSetup && (
          <input
            type="text" placeholder="Ad Soyad" value={name} onChange={e => setName(e.target.value)}
            style={inputStyle}
          />
        )}
        <input
          type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)}
          style={inputStyle}
        />
        <input
          type="password" placeholder="Şifre" value={password} onChange={e => setPassword(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleSubmit()}
          style={inputStyle}
        />

        {error && <div style={{ color: "#ef4444", fontSize: "13px", marginBottom: "12px", textAlign: "center" }}>{error}</div>}

        <button
          onClick={handleSubmit}
          disabled={loading}
          style={{
            width: "100%",
            padding: "14px",
            background: loading ? "rgba(0,255,239,0.1)" : "rgba(0,255,239,0.15)",
            border: "1px solid rgba(0,255,239,0.3)",
            color: "#00FFEF",
            borderRadius: "12px",
            fontSize: "15px",
            fontWeight: "700",
            cursor: loading ? "wait" : "pointer",
            fontFamily: "'Urbanist', sans-serif",
            transition: "all 0.2s",
          }}
        >
          {loading ? "..." : isSetup ? "Hesap Oluştur" : "Giriş"}
        </button>
      </div>
    </div>
  );
}

const inputStyle = {
  width: "100%",
  padding: "14px 16px",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.06)",
  color: "white",
  borderRadius: "12px",
  fontSize: "14px",
  marginBottom: "12px",
  fontFamily: "'Urbanist', sans-serif",
  outline: "none",
  boxSizing: "border-box",
};

// --- Home Page (Health Score) ---
function HomePage({ appId, period }) {
  const { fetchAPI } = useAPI();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!appId) return;
    setLoading(true);
    fetchAPI(`/api/health-score?app_id=${appId}&days=${period}`)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [appId, period]);

  if (loading) return <LoadingState />;
  if (!data) return <EmptyState message="Health score verisi yüklenemedi" />;

  return (
    <div>
      <h1 style={pageTitle}>App Health Score</h1>

      <div style={{ display: "flex", alignItems: "center", gap: "40px", marginBottom: "32px", flexWrap: "wrap" }}>
        <DonutChart value={data.healthScore} />
        <div style={{ flex: 1, minWidth: "240px" }}>
          <SignalBadge level={data.signal.level} text={data.signal.text} detail={data.signal.detail} />
          <div style={{ marginTop: "16px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
            {Object.entries(data.scores).map(([key, val]) => (
              <div key={key} style={{ background: "rgba(255,255,255,0.02)", borderRadius: "8px", padding: "10px 14px" }}>
                <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.4)", textTransform: "uppercase" }}>{key.replace(/([A-Z])/g, " $1")}</div>
                <div style={{ fontSize: "18px", fontWeight: "700", color: val >= 60 ? "#22c55e" : val >= 35 ? "#eab308" : "#ef4444" }}>{Math.round(val)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px" }}>
        <KPICard title="Yeni Abonelik" value={data.current?.new_subs || 0} change={data.previous?.new_subs ? ((data.current?.new_subs - data.previous.new_subs) / data.previous.new_subs * 100) : null} />
        <KPICard title="Renewal" value={data.current?.renewals || 0} change={data.previous?.renewals ? ((data.current?.renewals - data.previous.renewals) / data.previous.renewals * 100) : null} />
        <KPICard title="Proceeds" value={data.current?.total_proceeds || 0} prefix="$" change={data.previous?.total_proceeds ? ((data.current?.total_proceeds - data.previous.total_proceeds) / data.previous.total_proceeds * 100) : null} />
      </div>
    </div>
  );
}

// --- Revenue Page ---
function RevenuePage({ appId, period, country }) {
  const { fetchAPI } = useAPI();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!appId) return;
    setLoading(true);
    fetchAPI(`/api/revenue?app_id=${appId}&days=${period}${country !== "ALL" ? `&country=${country}` : ""}`)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [appId, period, country]);

  if (loading) return <LoadingState />;
  if (!data) return <EmptyState message="Revenue verisi yüklenemedi" />;

  const k = data.kpis;
  const proceedsChange = k.prevProceeds ? ((k.proceeds - k.prevProceeds) / k.prevProceeds * 100) : null;
  const payingChange = k.prevPayingUsers ? ((k.payingUsers - k.prevPayingUsers) / k.prevPayingUsers * 100) : null;

  const sparkProceeds = (data.dailyRevenue || []).map(d => d.proceeds || 0);
  const sparkDL = (data.dailyRevenue || []).map(d => d.downloads || 0);

  return (
    <div>
      <h1 style={pageTitle}>Revenue Intelligence</h1>

      {/* KPI Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px", marginBottom: "32px" }}>
        <KPICard title="Proceeds" value={k.proceeds?.toFixed(0)} prefix="$" change={proceedsChange} sparkData={sparkProceeds} />
        <KPICard title="MRR" value={k.mrr?.toFixed(0)} prefix="$" change={proceedsChange} />
        <KPICard title="Rev/Download" value={k.revPerDownload?.toFixed(2)} prefix="$" />
        <KPICard title="Paying Users" value={k.payingUsers} change={payingChange} />
      </div>

      {/* New vs Renewal Revenue */}
      <div style={sectionCard}>
        <h3 style={sectionTitle}>Nereden Geliyor?</h3>
        <div style={{ display: "flex", gap: "16px", marginBottom: "12px" }}>
          <span style={{ fontSize: "12px", color: "#00FFEF" }}>■ Yeni Sub</span>
          <span style={{ fontSize: "12px", color: "rgba(0,255,239,0.3)" }}>■ Renewal</span>
        </div>
        <StackedBar
          data={(data.revenueBreakdown || []).map(d => ({
            label: d.date?.slice(5) || "",
            new: d.new_revenue || 0,
            renewal: d.renewal_revenue || 0,
          }))}
        />
      </div>

      {/* Cohort Retention */}
      <div style={sectionCard}>
        <h3 style={sectionTitle}>Cohort Revenue Retention</h3>
        {(data.cohorts || []).length > 0 ? (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
              <thead>
                <tr>
                  <th style={thStyle}>Cohort</th>
                  {[...Array(7)].map((_, i) => <th key={i} style={thStyle}>M{i}</th>)}
                </tr>
              </thead>
              <tbody>
                {data.cohorts.map((c, i) => (
                  <tr key={i}>
                    <td style={{ ...tdStyle, fontWeight: "600" }}>{c.cohort_month}</td>
                    {["month_0", "month_1", "month_2", "month_3", "month_4", "month_5", "month_6"].map((m, j) => {
                      const pct = c.month_0 > 0 ? (c[m] / c.month_0 * 100) : 0;
                      return (
                        <td key={j} style={{
                          ...tdStyle,
                          background: `rgba(0,255,239,${pct / 200})`,
                          color: pct > 50 ? "white" : "rgba(255,255,255,0.5)",
                        }}>
                          {j === 0 ? c[m] : `${pct.toFixed(0)}%`}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ color: "rgba(255,255,255,0.3)", padding: "20px" }}>Henüz cohort verisi yok</div>
        )}
      </div>
    </div>
  );
}

// --- Country Page ---
function CountryPage({ appId, period }) {
  const { fetchAPI } = useAPI();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!appId) return;
    setLoading(true);
    fetchAPI(`/api/countries?app_id=${appId}&days=${period}`)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [appId, period]);

  if (loading) return <LoadingState />;
  if (!data) return <EmptyState message="Ülke verisi yüklenemedi" />;

  return (
    <div>
      <h1 style={pageTitle}>Country Intelligence</h1>

      {/* Quadrant */}
      <div style={sectionCard}>
        <h3 style={sectionTitle}>Quadrant Analizi</h3>
        <div style={{ display: "flex", gap: "16px", marginBottom: "12px", flexWrap: "wrap" }}>
          <span style={{ fontSize: "11px", color: "rgba(255,255,255,0.4)" }}>X: Impressions → | Y: Conversion Rate ↑ | Boyut: Revenue</span>
        </div>
        <QuadrantChart countries={data.countries || []} />
      </div>

      {/* Country Table */}
      <div style={sectionCard}>
        <h3 style={sectionTitle}>Ülke Karşılaştırma</h3>
        <CountryTable countries={data.countries || []} />
      </div>
    </div>
  );
}

// --- Subscriptions Page ---
function SubscriptionsPage({ appId, period }) {
  const { fetchAPI } = useAPI();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!appId) return;
    setLoading(true);
    fetchAPI(`/api/subscriptions?app_id=${appId}&days=${period}`)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [appId, period]);

  if (loading) return <LoadingState />;
  if (!data) return <EmptyState message="Subscription verisi yüklenemedi" />;

  return (
    <div>
      <h1 style={pageTitle}>Subscription Health</h1>

      {/* Flow Diagram */}
      <div style={sectionCard}>
        <h3 style={sectionTitle}>Subscription Akışı</h3>
        <SubscriptionFlow funnel={data.funnel} />
      </div>

      {/* Churn Timeline */}
      <div style={sectionCard}>
        <h3 style={sectionTitle}>Churn Alarm Sistemi</h3>
        <div style={{ display: "flex", gap: "16px", marginBottom: "12px" }}>
          <span style={{ fontSize: "12px", color: "#ef4444" }}>■ Voluntary</span>
          <span style={{ fontSize: "12px", color: "#f97316" }}>■ Involuntary</span>
        </div>
        <BarChart
          data={(data.churnTimeline || []).flatMap(d => [
            { label: d.date?.slice(5), value: d.voluntary || 0, color: "rgba(239,68,68,0.6)" },
            { label: "", value: d.involuntary || 0, color: "rgba(249,115,22,0.6)" },
          ])}
          height={160}
        />
        {/* Churn analysis */}
        {data.churnTimeline?.length > 2 && (() => {
          const recent = data.churnTimeline.slice(-7);
          const totalInv = recent.reduce((s, d) => s + (d.involuntary || 0), 0);
          const totalVol = recent.reduce((s, d) => s + (d.voluntary || 0), 0);
          if (totalInv > totalVol) {
            return <SignalBadge level="red" text="Billing issue artıyor" detail="Fiyat noktası sorun olabilir, involuntary churn yüksek" />;
          }
          if (totalVol > totalInv * 2) {
            return <SignalBadge level="yellow" text="Kullanıcılar aktif bırakıyor" detail="Onboarding/değer algısı sorunu olabilir" />;
          }
          return null;
        })()}
      </div>

      {/* Active Subs */}
      <div style={sectionCard}>
        <h3 style={sectionTitle}>Aktif Abonelikler</h3>
        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
          {(data.activeSubs || []).map((s, i) => (
            <div key={i} style={{ background: "rgba(0,255,239,0.05)", borderRadius: "12px", padding: "16px 24px", textAlign: "center" }}>
              <div style={{ fontSize: "24px", fontWeight: "700", color: "white" }}>{s.active?.toLocaleString()}</div>
              <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.4)", marginTop: "4px" }}>{s.subscription_plan || "Tümü"}</div>
              {s.trials > 0 && <div style={{ fontSize: "11px", color: "#eab308", marginTop: "2px" }}>{s.trials} trial</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// --- Alerts Page ---
function AlertsPage({ appId }) {
  const { fetchAPI } = useAPI();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!appId) return;
    fetchAPI(`/api/alerts?app_id=${appId}`)
      .then(d => setAlerts(d.alerts || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [appId]);

  if (loading) return <LoadingState />;

  return (
    <div>
      <h1 style={pageTitle}>Akıllı Uyarılar</h1>
      {alerts.length === 0 ? (
        <EmptyState message="Henüz uyarı yok — her şey yolunda görünüyor ✨" />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {alerts.map((a, i) => <AlertCard key={i} alert={a} />)}
        </div>
      )}
    </div>
  );
}

// --- Settings Page ---
function SettingsPage() {
  const { fetchAPI } = useAPI();
  const { user, logout } = useAuth();
  const [appForm, setAppForm] = useState({ id: "", name: "", bundleId: "" });
  const [apps, setApps] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteLink, setInviteLink] = useState("");
  const [syncDate, setSyncDate] = useState(new Date(Date.now() - 86400000).toISOString().split("T")[0]);
  const [status, setStatus] = useState("");

  // Load registered apps
  useEffect(() => {
    fetchAPI("/api/apps").then(d => setApps(d.apps || [])).catch(() => {});
  }, []);

  const addApp = async () => {
    try {
      await fetchAPI("/api/apps", { method: "POST", body: JSON.stringify(appForm) });
      setStatus("App eklendi!");
      setAppForm({ id: "", name: "", bundleId: "" });
      // Refresh list
      const d = await fetchAPI("/api/apps");
      setApps(d.apps || []);
    } catch (e) { setStatus(e.message); }
  };

  const invite = async () => {
    try {
      const res = await fetchAPI("/auth/invite", { method: "POST", body: JSON.stringify({ email: inviteEmail }) });
      setInviteLink(res.inviteLink);
      setInviteEmail("");
    } catch (e) { setStatus(e.message); }
  };

  const sync = async () => {
    setStatus("Syncing...");
    try {
      const res = await fetchAPI("/api/sync", { method: "POST", body: JSON.stringify({ date: syncDate }) });
      setStatus("Sync tamamlandı! ✓");
    } catch (e) { setStatus("Sync hatası: " + e.message); }
  };

  return (
    <div>
      <h1 style={pageTitle}>Ayarlar</h1>

      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "32px" }}>
        <div style={{ width: "40px", height: "40px", borderRadius: "12px", background: "rgba(0,255,239,0.15)", display: "flex", alignItems: "center", justifyContent: "center", color: "#00FFEF", fontWeight: "700", fontSize: "16px" }}>
          {user?.name?.charAt(0) || "?"}
        </div>
        <div>
          <div style={{ color: "white", fontWeight: "600" }}>{user?.name}</div>
          <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "13px" }}>{user?.email} · {user?.role}</div>
        </div>
      </div>

      {user?.role === "admin" && (
        <>
          {/* Registered Apps */}
          <div style={sectionCard}>
            <h3 style={sectionTitle}>Kayıtlı Uygulamalar</h3>
            {apps.length === 0 ? (
              <div style={{ color: "rgba(255,255,255,0.3)", fontSize: "13px", padding: "12px 0" }}>Henüz uygulama eklenmemiş</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {apps.map((app, i) => (
                  <div key={i} style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background: "rgba(255,255,255,0.03)",
                    borderRadius: "10px",
                    padding: "12px 16px",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <div style={{
                        width: "36px", height: "36px", borderRadius: "8px",
                        background: "rgba(0,255,239,0.1)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "16px",
                      }}>📱</div>
                      <div>
                        <div style={{ color: "white", fontWeight: "600", fontSize: "14px" }}>{app.name}</div>
                        <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "12px" }}>{app.bundle_id}</div>
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ color: "#00FFEF", fontSize: "12px", fontFamily: "monospace" }}>{app.id}</div>
                      <div style={{ color: "rgba(255,255,255,0.3)", fontSize: "11px" }}>{app.created_at?.split(" ")[0]}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add App */}
          <div style={sectionCard}>
            <h3 style={sectionTitle}>App Ekle</h3>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <input placeholder="Apple ID" value={appForm.id} onChange={e => setAppForm({ ...appForm, id: e.target.value })} style={{ ...inputStyle, flex: 1, marginBottom: 0 }} />
              <input placeholder="App Adı" value={appForm.name} onChange={e => setAppForm({ ...appForm, name: e.target.value })} style={{ ...inputStyle, flex: 1, marginBottom: 0 }} />
              <input placeholder="Bundle ID" value={appForm.bundleId} onChange={e => setAppForm({ ...appForm, bundleId: e.target.value })} style={{ ...inputStyle, flex: 1, marginBottom: 0 }} />
              <button onClick={addApp} style={btnAccent}>Ekle</button>
            </div>
          </div>

          {/* Invite */}
          <div style={sectionCard}>
            <h3 style={sectionTitle}>Kullanıcı Davet Et</h3>
            <div style={{ display: "flex", gap: "8px" }}>
              <input placeholder="Email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} style={{ ...inputStyle, flex: 1, marginBottom: 0 }} />
              <button onClick={invite} style={btnAccent}>Davet Et</button>
            </div>
            {inviteLink && (
              <div style={{ marginTop: "12px", padding: "12px", background: "rgba(0,255,239,0.05)", borderRadius: "8px", fontSize: "12px", color: "#00FFEF", wordBreak: "break-all" }}>
                {inviteLink}
              </div>
            )}
          </div>

          {/* Manual Sync */}
          <div style={sectionCard}>
            <h3 style={sectionTitle}>Manuel Sync</h3>
            <div style={{ display: "flex", gap: "8px" }}>
              <input type="date" value={syncDate} onChange={e => setSyncDate(e.target.value)} style={{ ...inputStyle, flex: 1, marginBottom: 0 }} />
              <button onClick={sync} style={btnAccent}>Sync</button>
            </div>
          </div>
        </>
      )}

      {status && <div style={{ marginTop: "12px", padding: "12px", background: "rgba(255,255,255,0.03)", borderRadius: "8px", color: "rgba(255,255,255,0.7)", fontSize: "13px" }}>{status}</div>}

      <button onClick={logout} style={{ ...btnAccent, background: "rgba(239,68,68,0.15)", borderColor: "rgba(239,68,68,0.3)", color: "#ef4444", marginTop: "24px" }}>
        Çıkış Yap
      </button>
    </div>
  );
}

// --- Shared Styles ---
const pageTitle = { color: "white", fontSize: "24px", fontWeight: "800", marginBottom: "24px", letterSpacing: "-0.5px" };
const sectionCard = { background: "rgba(255,255,255,0.02)", borderRadius: "16px", padding: "24px", marginBottom: "16px" };
const sectionTitle = { color: "rgba(255,255,255,0.8)", fontSize: "15px", fontWeight: "700", marginBottom: "16px" };
const thStyle = { padding: "8px 10px", textAlign: "left", color: "rgba(255,255,255,0.4)", fontWeight: "500", fontSize: "11px" };
const tdStyle = { padding: "8px 10px", color: "rgba(255,255,255,0.7)", fontSize: "12px" };
const btnAccent = {
  padding: "10px 20px",
  background: "rgba(0,255,239,0.1)",
  border: "1px solid rgba(0,255,239,0.25)",
  color: "#00FFEF",
  borderRadius: "10px",
  fontSize: "13px",
  fontWeight: "600",
  cursor: "pointer",
  fontFamily: "'Urbanist', sans-serif",
  whiteSpace: "nowrap",
};

function LoadingState() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 20px" }}>
      <div style={{ width: "32px", height: "32px", border: "2px solid rgba(0,255,239,0.2)", borderTopColor: "#00FFEF", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  );
}

function EmptyState({ message }) {
  return <div style={{ padding: "60px 20px", textAlign: "center", color: "rgba(255,255,255,0.3)", fontSize: "15px" }}>{message}</div>;
}

// ============================================
// MAIN APP
// ============================================

const NAV_ITEMS = [
  { id: "home", icon: "◉", label: "Health" },
  { id: "revenue", icon: "◈", label: "Revenue" },
  { id: "countries", icon: "◎", label: "Countries" },
  { id: "subs", icon: "◇", label: "Subs" },
  { id: "alerts", icon: "◆", label: "Alerts" },
  { id: "settings", icon: "⚙", label: "Ayarlar" },
];

function Dashboard() {
  const { fetchAPI } = useAPI();
  const [page, setPage] = useState("home");
  const [apps, setApps] = useState([]);
  const [selectedApp, setSelectedApp] = useState("");
  const [period, setPeriod] = useState(30);
  const [country, setCountry] = useState("ALL");
  const [plan, setPlan] = useState("ALL");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [alertCount, setAlertCount] = useState(0);

  useEffect(() => {
    fetchAPI("/api/apps").then(d => {
      setApps(d.apps || []);
      if (d.apps?.length > 0) setSelectedApp(d.apps[0].id);
    }).catch(console.error);
  }, []);

  useEffect(() => {
    if (!selectedApp) return;
    fetchAPI(`/api/alerts?app_id=${selectedApp}&unread=true`)
      .then(d => setAlertCount(d.alerts?.length || 0))
      .catch(() => {});
  }, [selectedApp]);

  const renderPage = () => {
    switch (page) {
      case "home": return <HomePage appId={selectedApp} period={period} />;
      case "revenue": return <RevenuePage appId={selectedApp} period={period} country={country} />;
      case "countries": return <CountryPage appId={selectedApp} period={period} />;
      case "subs": return <SubscriptionsPage appId={selectedApp} period={period} />;
      case "alerts": return <AlertsPage appId={selectedApp} />;
      case "settings": return <SettingsPage />;
      default: return <HomePage appId={selectedApp} period={period} />;
    }
  };

  return (
    <div style={{
      display: "flex",
      minHeight: "100vh",
      background: "#0A0A0B",
      fontFamily: "'Urbanist', sans-serif",
      color: "white",
    }}>
      {/* Sidebar */}
      <nav style={{
        width: sidebarOpen ? "220px" : "64px",
        background: "rgba(255,255,255,0.015)",
        borderRight: "1px solid rgba(255,255,255,0.04)",
        padding: "20px 12px",
        display: "flex",
        flexDirection: "column",
        transition: "width 0.3s ease",
        flexShrink: 0,
        position: "relative",
      }}>
        {/* Logo */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px", marginBottom: "32px", overflow: "hidden" }}>
          <svg width="28" height="16" viewBox="0 0 36 20" style={{ flexShrink: 0 }}>
            <path d="M0 20 Q18 -4 36 20" fill="none" stroke="#00FFEF" strokeWidth="2.5" />
            <circle cx="18" cy="6" r="3" fill="#00FFEF" opacity="0.4" />
          </svg>
          {sidebarOpen && <span style={{ fontSize: "16px", fontWeight: "800", color: "white", whiteSpace: "nowrap" }}>Insights</span>}
        </div>

        {/* App Selector */}
        {sidebarOpen && apps.length > 0 && (
          <select
            value={selectedApp}
            onChange={e => setSelectedApp(e.target.value)}
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "none",
              color: "white",
              padding: "10px 12px",
              borderRadius: "10px",
              fontSize: "13px",
              fontFamily: "'Urbanist', sans-serif",
              marginBottom: "24px",
              cursor: "pointer",
              width: "100%",
            }}
          >
            {apps.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        )}

        {/* Nav Items */}
        {NAV_ITEMS.map(item => (
          <button
            key={item.id}
            onClick={() => setPage(item.id)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "10px 14px",
              background: page === item.id ? "rgba(0,255,239,0.08)" : "transparent",
              border: "none",
              borderRadius: "10px",
              color: page === item.id ? "#00FFEF" : "rgba(255,255,255,0.45)",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: page === item.id ? "600" : "400",
              fontFamily: "'Urbanist', sans-serif",
              textAlign: "left",
              transition: "all 0.15s",
              marginBottom: "2px",
              position: "relative",
              width: "100%",
            }}
          >
            <span style={{ fontSize: "16px", width: "20px", textAlign: "center", flexShrink: 0 }}>{item.icon}</span>
            {sidebarOpen && <span>{item.label}</span>}
            {item.id === "alerts" && alertCount > 0 && (
              <span style={{
                background: "#ef4444",
                color: "white",
                fontSize: "10px",
                fontWeight: "700",
                borderRadius: "6px",
                padding: "2px 6px",
                marginLeft: "auto",
              }}>{alertCount}</span>
            )}
          </button>
        ))}

        {/* Collapse toggle */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          style={{
            position: "absolute",
            bottom: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            background: "rgba(255,255,255,0.04)",
            border: "none",
            color: "rgba(255,255,255,0.3)",
            width: "32px",
            height: "32px",
            borderRadius: "8px",
            cursor: "pointer",
            fontSize: "14px",
          }}
        >
          {sidebarOpen ? "«" : "»"}
        </button>
      </nav>

      {/* Main Content */}
      <main style={{ flex: 1, padding: "32px 40px", maxWidth: "1200px", overflow: "auto" }}>
        {page !== "settings" && (
          <FilterBar period={period} setPeriod={setPeriod} country={country} setCountry={setCountry} plan={plan} setPlan={setPlan} />
        )}
        {renderPage()}
      </main>

      {/* Global Font */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Urbanist:wght@300;400;500;600;700;800;900&display=swap');
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { background: #0A0A0B; overflow-x: hidden; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 3px; }
        select option { background: #1a1a1b; color: white; }
        input:focus { border-color: rgba(0,255,239,0.3) !important; }
      `}</style>
    </div>
  );
}

// --- Root App ---
export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

function AppContent() {
  const { user, loading } = useAuth();

  if (loading) return <LoadingState />;
  if (!user) return <LoginPage />;
  return <Dashboard />;
}
